<template>
  <div class="unit-info-container">
    <!-- 左侧蓝色标记和标题 -->
    <div class="page-header">
      <div class="blue-bar"></div>
      <h2 class="page-title">单位基本信息管理</h2>
    </div>
    
    <!-- 主要内容区域 -->
    <div class="page-content" v-loading="loading">
      <!-- 查看模式 -->
      <template v-if="!isEditing">
        <!-- 基本信息部分 -->
        <div class="info-section">
          <div class="section-header">
            <i class="el-icon-user-solid"></i>
            <span>基本信息</span>
          </div>
          
          <div class="info-table">
            <table>
              <tr>
                <td class="label">单位名称：</td>
                <td class="value">{{ unitInfo.unitName }}</td>
                <td class="label">主管理员姓名：</td>
                <td class="value">{{ unitInfo.contactPerson }}</td>
                <td class="label">电话：</td>
                <td class="value">{{ unitInfo.phone }}</td>
              </tr>
              <tr>
                <td class="label">单位简称：</td>
                <td class="value">{{ unitInfo.unitShortName }}</td>
                <td class="label">简称首字母：</td>
                <td class="value">{{ unitInfo.shortNameInitial }}</td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td class="label">税号：</td>
                <td class="value">{{ unitInfo.taxId }}</td>
                <td class="label">单位地址：</td>
                <td class="value">{{ unitInfo.address }}</td>
                <td></td>
                <td></td>
              </tr>
            </table>
          </div>
        </div>
        
        <!-- 银行信息部分 -->
        <div class="info-section">
          <div class="section-header">
            <i class="el-icon-message"></i>
            <span>银行信息</span>
          </div>
          
          <div class="bank-table-container">
            <el-table
              :data="bankInfos"
              border
              stripe
              style="width: 100%"
              :header-cell-style="{background:'#f5f7fa', color:'#606266'}"
              empty-text="暂无银行信息"
            >
              <el-table-column prop="bankName" label="银行名称" show-overflow-tooltip></el-table-column>
              <el-table-column prop="bankAccount" label="银行账号" show-overflow-tooltip></el-table-column>
              <el-table-column prop="bankBranch" label="开户行" show-overflow-tooltip></el-table-column>
              <el-table-column prop="accountType" label="账户类型" align="center">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.accountType === '基本户' ? 'primary' : 'info'" size="mini">
                    {{ scope.row.accountType }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="isPrimary" label="主要账户" align="center">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.isPrimary ? 'success' : 'info'" size="mini">
                    {{ scope.row.isPrimary ? '是' : '否' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="remark" label="备注" show-overflow-tooltip>
                <template slot-scope="scope">
                  {{ scope.row.remark || '-' }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
        
        <!-- 底部按钮区域 -->
        <div class="button-container">
          <el-button v-permission="'basic:unit-info:edit'" type="primary" class="edit-button" @click="handleEdit">编辑</el-button>
          <el-button type="success" class="subordinate-button" @click="handleSubordinateManagement">下级机构管理</el-button>
        </div>
      </template>
      
      <!-- 编辑模式 -->
      <template v-else>
        <el-form ref="unitForm" :model="editForm" :rules="rules" label-width="100px" class="edit-form">
          <!-- 基本信息部分 -->
          <div class="form-section">
            <div class="section-header">
              <i class="el-icon-user-solid"></i>
              <span>基本信息</span>
            </div>
            
            <div class="form-content">
              <!-- 第一行：单位名称，税号 -->
              <div class="form-row">
                <div class="form-column">
                  <div class="field-label">单位名称</div>
                  <el-form-item prop="unitName" class="no-margin">
                    <el-input v-model="editForm.unitName" placeholder="请输入单位名称"></el-input>
                  </el-form-item>
                </div>
                <div class="form-column">
                  <div class="field-label">税号</div>
                  <el-form-item prop="taxId" class="no-margin">
                    <el-input v-model="editForm.taxId" placeholder="请输入税号"></el-input>
                  </el-form-item>
                </div>
              </div>
              
              <!-- 第二行：单位简称，简称首字母 -->
              <div class="form-row">
                <div class="form-column">
                  <div class="field-label">单位简称</div>
                  <el-form-item prop="unitShortName" class="no-margin">
                    <el-input v-model="editForm.unitShortName" placeholder="请输入单位简称"></el-input>
                  </el-form-item>
                </div>
                <div class="form-column">
                  <div class="field-label">简称首字母</div>
                  <el-form-item prop="shortNameInitial" class="no-margin">
                    <el-input v-model="editForm.shortNameInitial" placeholder="请输入简称首字母"></el-input>
                  </el-form-item>
                </div>
              </div>
              
              <!-- 第三行：联系人，联系人电话 -->
              <div class="form-row">
                <div class="form-column">
                  <div class="field-label">联系人</div>
                  <el-form-item prop="contactPerson" class="no-margin">
                    <el-input v-model="editForm.contactPerson" placeholder="请输入联系人"></el-input>
                  </el-form-item>
                </div>
                <div class="form-column">
                  <div class="field-label">联系人电话</div>
                  <el-form-item prop="phone" class="no-margin">
                    <el-input v-model="editForm.phone" placeholder="请输入联系人电话"></el-input>
                  </el-form-item>
                </div>
              </div>
              
              <!-- 最后一行：单位地址 -->
              <div class="form-row">
                <div class="form-column">
                  <div class="field-label">单位地址</div>
                  <el-form-item prop="address" class="no-margin">
                    <el-input v-model="editForm.address" placeholder="请输入单位地址"></el-input>
                  </el-form-item>
                </div>
                <div class="form-column">
                  <!-- 空列 -->
                </div>
              </div>
            </div>
          </div>
          
          <!-- 银行信息管理部分 -->
          <div class="form-section">
            <div class="section-header bank-section-header">
              <div class="section-title">
                <i class="el-icon-message"></i>
                <span>银行信息管理</span>
              </div>
              <el-button type="primary" size="small" class="add-bank-btn" @click="handleAddBank">
                <i class="el-icon-plus"></i>
                新增银行
              </el-button>
            </div>
            
            <div class="bank-management">
              <el-table
                :data="bankInfos"
                border
                stripe
                style="width: 100%"
                :header-cell-style="{background:'#f5f7fa', color:'#606266'}"
                empty-text="暂无银行信息，点击新增银行按钮添加"
              >
                <el-table-column prop="bankName" label="银行名称" show-overflow-tooltip></el-table-column>
                <el-table-column prop="bankAccount" label="银行账号" show-overflow-tooltip></el-table-column>
                <el-table-column prop="bankBranch" label="开户行" show-overflow-tooltip></el-table-column>
                <el-table-column prop="accountType" label="账户类型" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.accountType === '基本户' ? 'primary' : 'info'" size="mini">
                      {{ scope.row.accountType }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="isPrimary" label="主要账户" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.isPrimary ? 'success' : 'info'" size="mini">
                      {{ scope.row.isPrimary ? '是' : '否' }}
                    </el-tag>
                  </template>
                </el-table-column>
                <el-table-column prop="remark" label="备注" show-overflow-tooltip>
                  <template slot-scope="scope">
                    {{ scope.row.remark || '-' }}
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="120" align="center">
                  <template slot-scope="scope">
                    <el-button type="text" size="mini" @click="handleEditBank(scope.$index)" class="edit-btn">编辑</el-button>
                    <el-button type="text" size="mini" @click="handleDeleteBank(scope.$index)" class="delete-btn">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
          
          <!-- 底部按钮 -->
          <div class="form-buttons">
            <el-button type="primary" @click="handleSubmit" class="submit-btn">完成</el-button>
            <el-button @click="handleCancel" class="cancel-btn">取消</el-button>
          </div>
        </el-form>
      </template>
    </div>
    
    <!-- 银行信息编辑对话框 -->
    <el-dialog 
      :title="bankEditIndex === -1 ? '新增银行信息' : '编辑银行信息'" 
      :visible.sync="showBankDialog"
      width="600px"
      :close-on-click-modal="false"
      :before-close="cancelBankForm">
      <el-form ref="bankForm" :model="bankForm" :rules="bankRules" label-width="100px">
        <el-form-item label="银行名称" prop="bankName">
          <el-input v-model="bankForm.bankName" placeholder="请输入银行名称"></el-input>
        </el-form-item>
        <el-form-item label="银行账号" prop="bankAccount">
          <el-input v-model="bankForm.bankAccount" placeholder="请输入银行账号"></el-input>
        </el-form-item>
        <el-form-item label="开户行" prop="bankBranch">
          <el-input v-model="bankForm.bankBranch" placeholder="请输入开户行"></el-input>
        </el-form-item>
        <el-form-item label="账户类型" prop="accountType">
          <el-select v-model="bankForm.accountType" placeholder="请选择账户类型" style="width: 100%">
            <el-option label="基本户" value="基本户"></el-option>
            <el-option label="一般户" value="一般户"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="主要账户">
          <el-switch v-model="bankForm.isPrimary"></el-switch>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="bankForm.remark" type="textarea" :rows="3" placeholder="请输入备注"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="cancelBankForm">取消</el-button>
        <el-button type="primary" @click="submitBankForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { query_unit_by_id, save_unit_info } from '@/api/modules/unit.js'

export default {
  name: 'UnitInfo',
  data() {
    return {
      isEditing: false,
      loading: false,
      unitId: null,
      unitInfo: {
        unitName: '',
        contactPerson: '',
        phone: '',
        taxId: '',
        address: '',
        unitShortName: '',
        shortNameInitial: ''
      },
      bankInfos: [],
      editForm: {
        unitName: '',
        contactPerson: '',
        phone: '',
        verifyCode: '',
        taxId: '',
        address: '',
        unitShortName: '',
        shortNameInitial: ''
      },
      bankForm: {
        bankName: '',
        bankAccount: '',
        bankBranch: '',
        accountType: '基本户',
        isPrimary: false,
        remark: ''
      },
      showBankDialog: false,
      bankEditIndex: -1,
      rules: {
        unitName: [
          { required: true, message: '请输入单位名称', trigger: 'blur' }
        ],
        contactPerson: [
          { required: true, message: '请输入主管理员姓名', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入电话号码', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        // 验证码验证规则暂时注释掉
        /*
        verifyCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' }
        ],
        */
        taxId: [
          { required: true, message: '请输入税号', trigger: 'blur' }
        ],
        address: [
          { required: true, message: '请输入单位地址', trigger: 'blur' }
        ],
        unitShortName: [
          { required: true, message: '请输入单位简称', trigger: 'blur' }
        ],
        shortNameInitial: [
          { required: true, message: '请输入简称首字母', trigger: 'blur' }
        ]
      },
      bankRules: {
        bankName: [
          { required: true, message: '请输入银行名称', trigger: 'blur' }
        ],
        bankAccount: [
          { required: true, message: '请输入银行账号', trigger: 'blur' }
        ],
        bankBranch: [
          { required: true, message: '请输入开户行', trigger: 'blur' }
        ],
        accountType: [
          { required: true, message: '请选择账户类型', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    // 组件创建时获取单位信息
    this.fetchUnitInfo()
  },
  methods: {
    // 获取单位信息
    fetchUnitInfo() {
      // 尝试从用户信息中获取unitId
      try {
        const userInfo = this.$store.state.userInfo || {}
        if (userInfo.unitId) {
          this.unitId = userInfo.unitId
} else {
          console.warn('未能从用户信息中获取unitId')
          this.$message.error('无法获取单位信息，请重新登录')
          return
        }
      } catch (err) {
        console.error('获取用户unitId出错:', err)
        this.$message.error('获取单位信息失败')
        return
      }
      
      if (!this.unitId) {
        console.warn('unitId未获取到，不执行API请求')
        return
      }
      
      this.loading = true
      
// 使用query_unit_by_id方法获取单位信息
      query_unit_by_id(this.unitId)
        .then(response => {
if (response.data && response.data.success) {
            const unitData = response.data.data
            // 存储真实的单位ID，用于后续更新操作
            if (unitData.id) {
              this.unitId = unitData.id
}
            // 更新单位信息 - 使用新的字段名
            this.unitInfo = {
              unitName: unitData.unitName,
              contactPerson: unitData.contactPerson,
              phone: unitData.phone,
              taxId: unitData.taxId,
              address: unitData.address,
              unitShortName: unitData.unitShortName || '',
              shortNameInitial: unitData.shortNameInitial || ''
            }
            // 更新银行信息
            this.bankInfos = unitData.bankInfos || []
          } else {
            console.warn('⚠️ 获取单位信息失败', response.data)
            this.$message.error(response.data.message || '获取单位信息失败')
          }
        })
        .catch(error => {
          console.error('❌ 获取单位信息错误', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            config: {
              url: error.config?.url,
              method: error.config?.method,
              headers: error.config?.headers,
              params: error.config?.params
            }
          })
          if (error.response?.status === 403) {
            this.$message.error('权限不足，无法获取单位信息 (403)')
          } else {
            this.$message.error('获取单位信息出错，请稍后重试')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleEdit() {
      this.isEditing = true;
      // 复制当前数据到编辑表单
      this.editForm = {
        unitName: this.unitInfo.unitName,
        contactPerson: this.unitInfo.contactPerson,
        phone: this.unitInfo.phone,
        verifyCode: '',
        taxId: this.unitInfo.taxId,
        address: this.unitInfo.address,
        unitShortName: this.unitInfo.unitShortName,
        shortNameInitial: this.unitInfo.shortNameInitial
      };
    },
    handleCancel() {
      this.isEditing = false;
      this.$message({
        message: '已取消编辑',
        type: 'info'
      });
    },
    // 跳转到下级机构管理（项目管理）
    handleSubordinateManagement() {
      this.$router.push('/basic/projects');
    },
    handleSubmit() {
      this.$refs.unitForm.validate(valid => {
        if (valid) {
          this.loading = true
          
          // 准备要提交的数据
          const formData = {
            unit_id: this.unitId,
            id: this.unitId,
            unitName: this.editForm.unitName,
            contactPerson: this.editForm.contactPerson,
            phone: this.editForm.phone,
            taxId: this.editForm.taxId,
            address: this.editForm.address,
            unitShortName: this.editForm.unitShortName,
            shortNameInitial: this.editForm.shortNameInitial,
            bankInfos: this.bankInfos,
            is_update: true // 标记为更新操作而非新增
          }
          
// 使用save_unit_info方法保存单位信息
          save_unit_info(formData)
            .then(response => {
if (response.data && response.data.success) {
                this.$message.success('单位信息更新成功')
                this.isEditing = false
                // 重新获取最新单位信息
                this.fetchUnitInfo()
              } else {
                console.warn('⚠️ 保存单位信息失败', response.data)
                this.$message.error(response.data.message || '保存单位信息失败')
              }
            })
            .catch(error => {
              console.error('❌ 保存单位信息错误', error)
              this.$message.error('保存单位信息出错，请稍后重试')
            })
            .finally(() => {
              this.loading = false
            })
        } else {
          console.warn('表单验证失败')
          return false
        }
      })
    },
    sendVerifyCode() {
      if (!this.editForm.phone) {
        this.$message({
          message: '请先输入手机号码',
          type: 'warning'
        });
        return;
      }
      
      // 模拟发送验证码
      this.$message({
        message: '验证码已发送至' + this.editForm.phone,
        type: 'success'
      });
    },
    // 银行信息管理方法
    handleAddBank() {
      this.bankForm = {
        bankName: '',
        bankAccount: '',
        bankBranch: '',
        accountType: '基本户',
        isPrimary: false,
        remark: ''
      };
      this.bankEditIndex = -1;
      this.showBankDialog = true;
    },
    handleEditBank(index) {
      this.bankForm = { ...this.bankInfos[index] };
      this.bankEditIndex = index;
      this.showBankDialog = true;
    },
    handleDeleteBank(index) {
      this.$confirm('确认要删除这条银行信息吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.bankInfos.splice(index, 1);
        this.$message.success('删除成功');
      }).catch(() => {
        this.$message.info('已取消删除');
      });
    },
    submitBankForm() {
      this.$refs.bankForm.validate(valid => {
        if (valid) {
          if (this.bankEditIndex === -1) {
            // 新增
            this.bankInfos.push({ ...this.bankForm });
          } else {
            // 编辑
            this.$set(this.bankInfos, this.bankEditIndex, { ...this.bankForm });
          }
          this.showBankDialog = false;
          this.$message.success(this.bankEditIndex === -1 ? '新增成功' : '编辑成功');
        }
      });
    },
    cancelBankForm() {
      this.showBankDialog = false;
    }
  }
}
</script>

<style lang="scss">
/* 全局样式覆盖，确保所有元素都是左对齐的 */
.unit-info-container {
  .el-form {
    .el-form-item {
      margin-bottom: 0;
      
      .el-form-item__label {
        text-align: left !important;
        padding: 0 !important;
      }
      
      .el-form-item__content {
        margin-left: 0 !important;
        text-align: left !important;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.unit-info-container {
  background-color: #fff;
  padding: 20px;
  min-height: calc(100vh - 120px);
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  
  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    
    .blue-bar {
      width: 4px;
      background-color: #1890ff;
      height: 20px;
      margin-right: 8px;
    }
    
    .page-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin: 0;
    }
  }
  
  // 查看模式样式
  .info-section {
    background-color: #f8f9fc;
    border-radius: 4px;
    padding: 15px 20px;
    margin-bottom: 20px;
    
    .section-header {
      display: flex;
      align-items: center;
      margin-bottom: 15px;
      
      i {
        color: #1890ff;
        font-size: 16px;
        margin-right: 6px;
      }
      
      span {
        font-size: 14px;
        font-weight: 500;
        color: #333;
      }
    }
    
    .info-table {
      table {
        width: 100%;
        border-collapse: collapse;
        
        tr {
          &:not(:last-child) {
            border-bottom: 1px solid #ebeef5;
          }
          
          td {
            padding: 15px 5px;
            vertical-align: middle;
            
            &.label {
              width: 90px;
              text-align: right;
              color: #606266;
              font-size: 14px;
              white-space: nowrap;
            }
            
            &.value {
              min-width: 180px;
              max-width: 220px;
              color: #303133;
              font-size: 14px;
              padding-left: 10px;
              padding-right: 30px;
            }
          }
        }
      }
    }
  }
  
  // 编辑模式样式
  .edit-form {
    .form-section {
      background-color: #f9f9f9;
      border-radius: 4px;
      padding: 20px;
      margin-bottom: 20px;
      
      :deep(.el-form-item__label) {
        text-align: left;
        padding-left: 0;
      }
      
      :deep(.el-form-item__content) {
        margin-left: 0 !important;
        text-align: left;
      }
      
      .section-header {
        display: flex;
        align-items: center;
        margin-bottom: 25px;
        color: #1890ff;
        
        i {
          font-size: 18px;
          margin-right: 8px;
        }
        
        span {
          font-size: 14px;
          font-weight: 500;
        }
      }
      
      .form-content {
        .form-row {
          display: flex;
          margin-bottom: 20px;
          
          .form-column {
            flex: 1;
            margin-right: 20px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            
            &:last-child {
              margin-right: 0;
            }
            
            .field-label {
              width: 100%;
              font-size: 14px;
              color: #606266;
              margin-bottom: 8px;
              text-align: left;
              padding-left: 0;
            }
            
            .no-margin {
              margin: 0;
              width: 100%;
            }
      
      :deep(.el-form-item__content) {
              display: block;
              text-align: left;
              margin-left: 0 !important;
              width: 100%;
        }
        
            :deep(.el-input) {
              width: 100%;
              text-align: left;
            }
            
            :deep(.el-input__inner) {
              height: 36px;
              line-height: 36px;
              border: 1px solid #dcdfe6;
              border-radius: 3px;
              padding: 0 10px;
              text-align: left;
              
              &[disabled] {
                background-color: #f5f7fa;
                border-color: #e4e7ed;
                color: #606266;
                cursor: not-allowed;
              }
            }
          }
        }
      }
    }
    
    .form-buttons {
      display: flex;
      justify-content: center;
      margin-top: 40px;
      
      .submit-btn {
        width: 120px;
        height: 40px;
        margin-right: 20px;
        background-color: #1890ff;
        border-color: #1890ff;
        font-size: 14px;
      }
      
      .cancel-btn {
        width: 120px;
        height: 40px;
        border: 1px solid #dcdfe6;
        color: #606266;
        font-size: 14px;
      }
    }
  }
  
  // 银行信息表格样式
  .bank-table-container {
    .el-table {
      font-size: 14px;
      
      .el-table__empty-text {
        color: #909399;
      }
    }
  }
  
  // 编辑模式下的银行信息管理样式
  .bank-management {
    .el-table {
      font-size: 14px;
      
      .el-table__empty-text {
        color: #909399;
      }
      
      .edit-btn {
        color: #1890ff;
        margin-right: 8px;
        
        &:hover {
          color: #40a9ff;
        }
      }
      
      .delete-btn {
        color: #f56c6c;
        
        &:hover {
          color: #f78989;
        }
      }
    }
  }
  
  // 银行信息管理标题栏样式
  .bank-section-header {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    
    .section-title {
      display: flex;
      align-items: center;
      
      i {
        color: #1890ff;
        font-size: 18px;
        margin-right: 8px;
      }
      
      span {
        font-size: 14px;
        font-weight: 500;
      }
    }
    
    .add-bank-btn {
      background-color: #1890ff;
      border-color: #1890ff;
      font-size: 12px;
      
      &:hover, &:focus {
        background-color: #40a9ff;
        border-color: #40a9ff;
      }
      
      i {
        margin-right: 4px;
      }
    }
  }
  
  .button-container {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 30px;
    
    .edit-button,
    .subordinate-button {
      width: 140px;
      height: 40px;
      font-size: 14px;
      font-weight: 400;
      border-radius: 4px;
      
      &:hover, &:focus {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }
    }
    
    .edit-button {
      background-color: #1890ff;
      border-color: #1890ff;
      
      &:hover, &:focus {
        background-color: #40a9ff;
        border-color: #40a9ff;
      }
    }
    
    .subordinate-button {
      background-color: #52c41a;
      border-color: #52c41a;
      
      &:hover, &:focus {
        background-color: #73d13d;
        border-color: #73d13d;
      }
    }
  }
}
</style> 